import { useState, useCallback, useMemo } from 'react';
import type { Note, NoteCategory } from '../../types/driver';
import { ShapeAwareList, ShapeAwareItem } from '../ShapeAwareList';
import {
  getNoteCategoryLabel,
  getCategoryTabColor,
  formatRelativeTime,
} from '../../services/mockData';
import '../NotesHistory/notes-history.css';

/**
 * TabbedNotes - Digital Clipboard v3 Design
 *
 * CRITICAL: Structure must match for dock clipping:
 *   <section className="notes-history"> + padding via CSS
 *     <header>
 *     <ShapeAwareList>
 */

interface TabbedNotesProps {
  notes: Note[];
  onAddNote?: () => void;
  onNoteClick?: (note: Note) => void;
  className?: string;
  /** When true, header is rendered externally via NotesHeader */
  externalHeader?: boolean;
}

/**
 * Props for the standalone NotesHeader component
 */
interface NotesHeaderProps {
  noteCount: number;
  showKey: boolean;
  onToggleKey: () => void;
  onAddNote?: () => void;
}

/**
 * Category color definitions for the key legend
 */
const CATEGORY_COLORS: { category: NoteCategory; label: string }[] = [
  { category: 'safety', label: 'Safety' },
  { category: 'performance', label: 'Performance' },
  { category: 'customer', label: 'Customer' },
  { category: 'equipment', label: 'Equipment' },
  { category: 'training', label: 'Training' },
  { category: 'general', label: 'General' },
  { category: 'disciplinary', label: 'Disciplinary' },
  { category: 'commendation', label: 'Commendation' },
];

/* ===========================================
   ICON COMPONENTS
   Defined early so they can be used in NotesHeader
   =========================================== */

function PlusIcon(): JSX.Element {
  return (
    <svg className="notes-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function InfoIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

function PinIcon(): JSX.Element {
  return (
    <svg
      className="note-record-pin-icon"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
    </svg>
  );
}

/**
 * NotesHeader - Standalone header for VortexLayout's headerLeft slot
 *
 * Renders the notes panel header content (title, count, Key button, Add button)
 * without any vortex-aware positioning - VortexLayout handles that.
 */
export function NotesHeader({
  noteCount,
  showKey,
  onToggleKey,
  onAddNote,
}: NotesHeaderProps): JSX.Element {
  return (
    <div className="notes-header notes-header--standalone">
      <div className="notes-header-text">
        <h3 className="notes-title">Notes</h3>
        <span className="notes-count">{noteCount} total</span>
      </div>

      <div className="header-actions">
        <button
          type="button"
          className="key-toggle"
          onClick={onToggleKey}
          aria-label={showKey ? 'Hide color key' : 'Show color key'}
          aria-pressed={showKey}
        >
          <InfoIcon />
          <span>Key</span>
        </button>

        {onAddNote && (
          <button
            type="button"
            className="notes-add-button"
            onClick={onAddNote}
            aria-label="Add new note"
          >
            <PlusIcon />
            <span>Add</span>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Format full timestamp for expanded view
 */
function formatFullTimestamp(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' at ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Props for TabbedNotesBody (header-less version)
 */
interface TabbedNotesBodyProps {
  notes: Note[];
  onNoteClick?: (note: Note) => void;
  showKey: boolean;
  className?: string;
}

/**
 * TabbedNotesBody - Body-only version for use with external header
 *
 * Renders the color key legend and notes list without the header.
 * Used when NotesHeader is rendered in VortexLayout's headerLeft slot.
 */
export function TabbedNotesBody({
  notes,
  onNoteClick,
  showKey,
  className = '',
}: TabbedNotesBodyProps): JSX.Element {
  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      if (a.isPinned && !b.isPinned) {return -1;}
      if (!a.isPinned && b.isPinned) {return 1;}
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [notes]);

  return (
    <section className={`notes-history notes-history--no-header ${className}`} aria-label="Driver Notes">
      {/* Color Key Legend */}
      <div className={`color-key ${showKey ? 'visible' : ''}`}>
        <div className="color-key-grid">
          {CATEGORY_COLORS.map(({ category, label }) => (
            <div key={category} className="color-key-item">
              <span
                className="color-key-swatch"
                style={{ backgroundColor: getCategoryTabColor(category) }}
              />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Notes List - Shape-aware for dock cutout */}
      <ShapeAwareList panelSide="left" className="notes-list">
        {sortedNotes.length === 0 ? (
          <div className="notes-empty">
            <p>No notes yet</p>
          </div>
        ) : (
          sortedNotes.map((note) => (
            <ShapeAwareItem key={note.id}>
              <NoteRecord note={note} onClick={onNoteClick} />
            </ShapeAwareItem>
          ))
        )}
      </ShapeAwareList>
    </section>
  );
}

export function TabbedNotes({
  notes,
  onAddNote,
  onNoteClick,
  className = '',
  externalHeader = false,
}: TabbedNotesProps): JSX.Element {
  const [showKey, setShowKey] = useState(false);

  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      if (a.isPinned && !b.isPinned) {return -1;}
      if (!a.isPinned && b.isPinned) {return 1;}
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [notes]);

  const handleToggleKey = useCallback(() => {
    setShowKey((prev) => !prev);
  }, []);

  // When using external header, render body only
  if (externalHeader) {
    return (
      <TabbedNotesBody
        notes={notes}
        onNoteClick={onNoteClick}
        showKey={showKey}
        className={className}
      />
    );
  }

  return (
    <section className={`notes-history ${className}`} aria-label="Driver Notes">
      {/* Header */}
      <div className="notes-header">
        <div className="notes-header-text">
          <h3 className="notes-title">Notes</h3>
          <span className="notes-count">{notes.length} total</span>
        </div>

        <div className="header-actions">
          <button
            type="button"
            className="key-toggle"
            onClick={handleToggleKey}
            aria-label={showKey ? 'Hide color key' : 'Show color key'}
            aria-pressed={showKey}
          >
            <InfoIcon />
            <span>Key</span>
          </button>

          {onAddNote && (
            <button
              type="button"
              className="notes-add-button"
              onClick={onAddNote}
              aria-label="Add new note"
            >
              <PlusIcon />
              <span>Add</span>
            </button>
          )}
        </div>
      </div>

      {/* Color Key Legend */}
      <div className={`color-key ${showKey ? 'visible' : ''}`}>
        <div className="color-key-grid">
          {CATEGORY_COLORS.map(({ category, label }) => (
            <div key={category} className="color-key-item">
              <span
                className="color-key-swatch"
                style={{ backgroundColor: getCategoryTabColor(category) }}
              />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Notes List - Shape-aware for dock cutout */}
      <ShapeAwareList panelSide="left" className="notes-list">
        {sortedNotes.length === 0 ? (
          <div className="notes-empty">
            <p>No notes yet</p>
          </div>
        ) : (
          sortedNotes.map((note) => (
            <ShapeAwareItem key={note.id}>
              <NoteRecord note={note} onClick={onNoteClick} />
            </ShapeAwareItem>
          ))
        )}
      </ShapeAwareList>
    </section>
  );
}

/**
 * Individual note record (Digital Clipboard v3 design)
 */
function NoteRecord({ note, onClick }: { note: Note; onClick?: (note: Note) => void }): JSX.Element {
  const handleClick = useCallback(() => onClick?.(note), [note, onClick]);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(note);
      }
    },
    [note, onClick]
  );

  const handleActionClick = useCallback(
    (event: React.MouseEvent, action: string) => {
      event.stopPropagation();
      console.log(`Action: ${action} on note ${note.id}`);
    },
    [note.id]
  );

  const tabColor = getCategoryTabColor(note.category);
  const categoryLabel = getNoteCategoryLabel(note.category);

  return (
    <article
      className={`note-record ${note.isPinned ? 'pinned' : ''}`}
      data-category={note.category}
      data-priority={note.priority}
      style={{ '--tab-color': tabColor } as React.CSSProperties}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${categoryLabel} note: ${note.content.slice(0, 50)}...`}
    >
      {/* Collapsed row */}
      <div className="note-record-row">
        {note.isPinned && (
          <span className="note-record-pin">
            <PinIcon />
          </span>
        )}
        <span className="note-record-content">{note.content}</span>
        <div className="note-record-right">
          {note.isFollowUp && <span className="note-record-followup" />}
          <span className="note-record-author">{note.createdByName}</span>
          <span className="note-record-time">{formatRelativeTime(note.createdAt)}</span>
        </div>
      </div>

      {/* Expanded content (revealed on hover) */}
      <div className="note-record-expanded">
        <div className="note-record-category-label" style={{ color: tabColor }}>
          {categoryLabel}
        </div>
        <p className="note-record-full-content">{note.content}</p>

        {note.tags && note.tags.length > 0 && (
          <div className="note-record-tags">
            {note.tags.map((tag) => (
              <span key={tag} className="note-record-tag">
                #{tag}
              </span>
            ))}
          </div>
        )}

        <div className="note-record-footer">
          <div className="note-record-footer-left">
            <span>{note.createdByName}</span>
            <span>{formatFullTimestamp(note.createdAt)}</span>
          </div>
          <div className="note-record-footer-actions">
            <button
              type="button"
              className="note-record-action"
              onClick={(e) => handleActionClick(e, 'edit')}
            >
              Edit
            </button>
            <button
              type="button"
              className="note-record-action"
              onClick={(e) => handleActionClick(e, note.isPinned ? 'unpin' : 'pin')}
            >
              {note.isPinned ? 'Unpin' : 'Pin'}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default TabbedNotes;
